import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, EmptyState, StatusBadge, ConfirmModal } from '../components/common';
import { formatDate } from '../utils/formatters';

/**
 * AllSessions page - View all past sessions for a meeting
 */
const AllSessions = ({ meeting, user, onBack, onViewSummary, onResumeSession }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    loadSessions();
  }, [meeting.id]);

  const loadSessions = async () => {
    try {
      const data = await api.get(`/sessions/meeting/${meeting.id}`);
      setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = (sessionId) => {
    setConfirmState({
      message: 'Are you sure you want to delete this session? This action cannot be undone.',
      confirmLabel: 'Delete Session',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/sessions/${sessionId}`);
          loadSessions();
        } catch (error) {
          console.error('Failed to delete session:', error);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">All Sessions - {meeting.name}</h1>
        <Button variant="secondary" onClick={onBack}>
          ← Back to Meeting
        </Button>
      </div>

      {sessions.length === 0 ? (
        <EmptyState
          icon="📅"
          title="No sessions yet"
          message="Start a meeting to create your first session"
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date Started</th>
              <th>Date Ended</th>
              <th>Topics Discussed</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => {
              const started = new Date(session.startedAt);
              const ended = session.endedAt ? new Date(session.endedAt) : null;
              const duration = ended 
                ? Math.round((ended - started) / 1000 / 60) + ' min'
                : 'In Progress';

              return (
                <tr key={session.id}>
                  <td>{formatDate(session.startedAt)}</td>
                  <td>{session.endedAt ? formatDate(session.endedAt) : '-'}</td>
                  <td>
                    {session.discussedTopics?.length || 0} topics
                    {duration !== 'In Progress' && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.85rem', 
                        color: 'var(--color-text-muted)' 
                      }}>
                        ({duration})
                      </span>
                    )}
                  </td>
                  <td>
                    {session.endedAt ? (
                      <StatusBadge status="Done" type="todo" />
                    ) : (
                      <span className="badge" style={{ 
                        backgroundColor: 'var(--color-success)', 
                        color: 'white' 
                      }}>
                        In Progress
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                      {!session.endedAt && onResumeSession && (user.isAdmin || user.isManager) && (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => onResumeSession(session)}
                        >
                          Resume
                        </Button>
                      )}
                      {session.endedAt && onViewSummary && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => onViewSummary(session)}
                        >
                          View Summary
                        </Button>
                      )}
                      {user.isAdmin && (
                        <Button
                          variant="secondary"
                          size="small"
                          style={{ color: 'var(--color-danger)' }}
                          onClick={() => handleDeleteSession(session.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

AllSessions.propTypes = {
  meeting: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onViewSummary: PropTypes.func,
  onResumeSession: PropTypes.func,
};

export default AllSessions;
